import { useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@evos/components";
import SetSessionLimitSlider from "./SetSessionLimitSlider";
import type { SetVoltageOutputArguments, SetSessionLimitArguments } from "../types";

type ConfigureDeviceFormProps = {
  formDefaultValues:
    | { site_ref: string; device_id: string; limit: number }
    | { serial: string; charge: number; circuit_amp_limit: number };
  deviceType: "charger" | "distBoard" | "subDistBoard" | "circuit";
  submitLoading: boolean;
  onSubmit: (data: SetVoltageOutputArguments | SetSessionLimitArguments) => void;
  closeForm: () => void;
};

export default function ConfigureDeviceForm({
  formDefaultValues,
  deviceType,
  submitLoading,
  onSubmit,
  closeForm,
}: ConfigureDeviceFormProps) {
  const { register, handleSubmit, getValues, resetField, control, formState: { isDirty, isSubmitSuccessful } } = useForm<
    SetVoltageOutputArguments | SetSessionLimitArguments
  >({
    defaultValues: formDefaultValues,
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      if ("limit" in formDefaultValues && formDefaultValues.limit === getValues("limit")) {
        resetField("limit", { defaultValue: getValues("limit") });
      } else if ("charge" in formDefaultValues && formDefaultValues.charge === getValues("charge")) {
        resetField("charge", { defaultValue: getValues("charge") });
      }
    }
  }, [isSubmitSuccessful, formDefaultValues, getValues, resetField]);

  return (
    <div id="ConfigureDeviceForm">
      <Form onSubmit={handleSubmit((data) => onSubmit(data as SetVoltageOutputArguments & SetSessionLimitArguments))}>
        {deviceType === "charger" && "serial" in formDefaultValues ? (
          <Form.Group>
            <Form.Label className="fw-semibold">Session amp limit</Form.Label>
            <Controller
              name="charge"
              control={control}
              shouldUnregister
              render={({ field: { onChange, value } }) => (
                <SetSessionLimitSlider
                  serial={formDefaultValues.serial}
                  circuitAmpLimit={formDefaultValues.circuit_amp_limit ?? 32}
                  currentSessionLimit={value}
                  onChange={onChange}
                  disabled={submitLoading}
                />
              )}
            />
          </Form.Group>
        ) : (deviceType === "distBoard" || deviceType === "subDistBoard") && "limit" in formDefaultValues ? (
          <Form.Group>
            <Form.Label className="fw-semibold">Dist board max output (A)</Form.Label>
            <Form.Control
              {...register("limit", { required: true, valueAsNumber: true })}
              type="number"
              placeholder="Max output"
            />
          </Form.Group>
        ) : null}
        <div className="d-flex justify-content-center pt-4 pb-3">
          <Button disabled={!isDirty} isLoading={submitLoading} type="submit">
            Apply changes
          </Button>
        </div>
        <div className="d-flex justify-content-center pb-3">
          <p className="text-primary text-decoration-underline mb-0" style={{ cursor: "pointer" }} onClick={closeForm}>
            Cancel
          </p>
        </div>
      </Form>
    </div>
  );
}
